import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  FaCalendarAlt,
  FaCheckCircle,
  FaClock,
  FaDownload,
  FaExclamationTriangle,
  FaFileInvoice,
  FaFileSignature,
  FaRedoAlt,
  FaShieldAlt,
  FaSyncAlt,
} from "react-icons/fa";

import {
  useLocation,
  useNavigate,
} from "react-router-dom";

import { useUser } from "../../Home/UserContext";
import { useNotification } from "../../../components/Notification/NotificationManager";

import useScrollToTop from "../../../hooks/useScollToTop";

import {
  getReglementsByUser,
} from "../../../services/achatsServices";

import {
  createCheckoutSessionForPlan,
} from "../../../services/paymentServices";

import {
  findSubscriptionPlan,
} from "../../../data/subscriptionPlans";

import {
  formatSubscriptionDate,
  getLatestPaidPurchase,
  getSubscriptionProgress,
  isPaidPurchase,
} from "../../../utils/subscription.utils";

const API_URL =
  process.env.REACT_APP_API_URL ||
  "http://localhost:3318";

const currencyFormatter = new Intl.NumberFormat(
  "fr-FR",
  {
    style: "currency",
    currency: "EUR",
  },
);

const toneStyles = {
  emerald: {
    badge:
      "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300",
    bar: "bg-emerald-500",
    icon:
      "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300",
  },

  amber: {
    badge:
      "bg-amber-100 text-amber-800 dark:bg-amber-500/10 dark:text-amber-300",
    bar: "bg-amber-500",
    icon:
      "bg-amber-100 text-amber-700 dark:bg-amber-500/10 dark:text-amber-300",
  },

  rose: {
    badge:
      "bg-rose-100 text-rose-700 dark:bg-rose-500/10 dark:text-rose-300",
    bar: "bg-rose-500",
    icon:
      "bg-rose-100 text-rose-700 dark:bg-rose-500/10 dark:text-rose-300",
  },

  slate: {
    badge:
      "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
    bar: "bg-slate-500",
    icon:
      "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
  },
};

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

function resolveDocumentUrl(value) {
  if (!value) return null;

  if (
    value.startsWith("http://") ||
    value.startsWith("https://")
  ) {
    return value;
  }

  if (value.startsWith("/")) {
    return `${API_URL}${value}`;
  }

  return `${API_URL}/${value}`;
}

function getStoredUser() {
  try {
    return JSON.parse(
      localStorage.getItem("userInfo") || "null",
    );
  } catch (error) {
    return null;
  }
}

function DocumentCard({
  icon: Icon,
  title,
  description,
  url,
  badge,
}) {
  const resolvedUrl = resolveDocumentUrl(url);

  return (
    <article className="flex flex-col rounded-3xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="flex items-start gap-4">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-blue-50 text-xl text-blue-600 dark:bg-blue-500/10 dark:text-blue-300">
          <Icon />
        </div>

        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-bold text-slate-900 dark:text-white">
              {title}
            </h3>

            {badge ? (
              <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                {badge}
              </span>
            ) : null}
          </div>

          <p className="mt-1 text-sm leading-6 text-slate-500 dark:text-slate-400">
            {description}
          </p>
        </div>
      </div>

      <div className="mt-5 flex-1" />

      {resolvedUrl ? (
        <a
          href={resolvedUrl}
          target="_blank"
          rel="noopener noreferrer"
          download
          className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-blue-600 px-4 py-3 font-semibold text-white transition hover:bg-blue-500"
        >
          <FaDownload />
          Télécharger le document
        </a>
      ) : (
        <div className="rounded-2xl bg-slate-100 px-4 py-3 text-center text-sm font-medium text-slate-500 dark:bg-slate-800 dark:text-slate-400">
          Document non disponible
        </div>
      )}
    </article>
  );
}

export default function MySubscriptionPage() {
  useScrollToTop();

  const { user } = useUser();
  const { addNotification } = useNotification();

  const navigate = useNavigate();
  const location = useLocation();

  const storedUser = getStoredUser();
  const effectiveUser = user || storedUser;

  const userId =
    effectiveUser?.userId ||
    effectiveUser?.id ||
    null;

  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [renewing, setRenewing] = useState(false);

  const loadPurchases = useCallback(async () => {
    if (!userId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      const response =
        await getReglementsByUser(userId);

      setPurchases(
        Array.isArray(response) ? response : [],
      );
    } catch (error) {
      console.error(
        "Erreur lors du chargement de l'abonnement :",
        error,
      );

      addNotification(
        "Impossible de charger votre abonnement.",
        "error",
      );
    } finally {
      setLoading(false);
    }
  }, [addNotification, userId]);

  useEffect(() => {
    loadPurchases();
  }, [loadPurchases]);

  useEffect(() => {
    const searchParams = new URLSearchParams(
      location.search,
    );

    if (searchParams.get("success") === "true") {
      addNotification(
        "Votre paiement a été accepté. Votre renouvellement apparaîtra après validation du paiement.",
        "success",
      );

      navigate("/appli/mon-abonnement", {
        replace: true,
      });

      loadPurchases();
    }

    if (searchParams.get("canceled") === "true") {
      addNotification(
        "Le paiement a été annulé.",
        "warning",
      );

      navigate("/appli/mon-abonnement", {
        replace: true,
      });
    }
  }, [
    addNotification,
    loadPurchases,
    location.search,
    navigate,
  ]);

  const currentPurchase = useMemo(
    () => getLatestPaidPurchase(purchases),
    [purchases],
  );

  const subscription = useMemo(() => {
    if (!currentPurchase) return null;

    return getSubscriptionProgress(
      currentPurchase,
    );
  }, [currentPurchase]);

  const currentPlan = useMemo(() => {
    if (!currentPurchase) return null;

    return findSubscriptionPlan(
      currentPurchase.formule,
    );
  }, [currentPurchase]);

  const purchaseHistory = useMemo(() => {
    return [...purchases].sort(
      (first, second) =>
        new Date(second.date_achat).getTime() -
        new Date(first.date_achat).getTime(),
    );
  }, [purchases]);

  const handleRenew = async () => {
    if (!userId || !currentPlan) {
      addNotification(
        "La formule actuelle ne peut pas être renouvelée en ligne.",
        "error",
      );
      return;
    }

    if (
      !currentPlan.product_id ||
      !currentPlan.amount
    ) {
      navigate("/formules-tarifs");
      return;
    }

    try {
      setRenewing(true);

      const checkoutUrl =
        await createCheckoutSessionForPlan(
          {
            role_id: currentPlan.role_id,
            formule: currentPlan.formule,
            price: String(currentPlan.amount),
            product_id: currentPlan.product_id,

            // À exploiter dans le contrôleur Stripe
            // pour revenir sur cette page.
            return_path: "/appli/mon-abonnement",

            // Utile pour identifier un renouvellement.
            renewal_of_achat_id:
              currentPurchase.id,
          },
          userId,
        );

      window.location.href = checkoutUrl;
    } catch (error) {
      console.error(
        "Erreur lors du renouvellement :",
        error,
      );

      addNotification(
        "Impossible de démarrer le paiement. Veuillez réessayer.",
        "error",
      );
    } finally {
      setRenewing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 px-4 pb-16 pt-28 dark:bg-slate-950">
        <div className="mx-auto max-w-6xl space-y-4">
          <div className="h-32 animate-pulse rounded-3xl bg-slate-200 dark:bg-slate-800" />
          <div className="h-72 animate-pulse rounded-3xl bg-slate-200 dark:bg-slate-800" />
        </div>
      </div>
    );
  }

  if (!userId) {
    return (
      <div className="min-h-screen bg-slate-50 px-4 pb-16 pt-28 dark:bg-slate-950">
        <div className="mx-auto max-w-xl rounded-3xl border border-slate-200 bg-white p-8 text-center shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <FaShieldAlt className="mx-auto text-4xl text-blue-600" />

          <h1 className="mt-4 text-2xl font-black text-slate-900 dark:text-white">
            Connexion nécessaire
          </h1>

          <p className="mt-3 text-sm text-slate-600 dark:text-slate-400">
            Connectez-vous pour consulter votre
            abonnement et vos documents.
          </p>

          <button
            type="button"
            onClick={() => navigate("/login")}
            className="mt-6 rounded-2xl bg-blue-600 px-5 py-3 font-semibold text-white transition hover:bg-blue-500"
          >
            Se connecter
          </button>
        </div>
      </div>
    );
  }

  if (!currentPurchase || !subscription) {
    return (
      <div className="min-h-screen bg-slate-50 px-4 pb-16 pt-28 dark:bg-slate-950">
        <div className="mx-auto max-w-3xl rounded-3xl border border-slate-200 bg-white p-8 text-center shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <FaExclamationTriangle className="mx-auto text-4xl text-amber-500" />

          <h1 className="mt-4 text-2xl font-black text-slate-900 dark:text-white">
            Aucun abonnement actif
          </h1>

          <p className="mt-3 text-sm text-slate-600 dark:text-slate-400">
            Aucun achat réglé n’est actuellement
            associé à votre compte.
          </p>

          <button
            type="button"
            onClick={() =>
              navigate("/formules-tarifs")
            }
            className="mt-6 rounded-2xl bg-blue-600 px-5 py-3 font-semibold text-white transition hover:bg-blue-500"
          >
            Découvrir les formules
          </button>
        </div>
      </div>
    );
  }

  const tone =
    toneStyles[subscription.tone] ||
    toneStyles.slate;

  return (
    <div className="min-h-screen bg-slate-50 pb-16 pt-28 text-slate-900 dark:bg-slate-950 dark:text-slate-100">
      <div className="mx-auto w-full max-w-6xl px-4 sm:px-6">
        <header className="mb-7 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-blue-600 dark:text-blue-400">
              Espace client
            </p>

            <h1 className="mt-2 text-3xl font-black tracking-tight sm:text-4xl">
              Mon abonnement
            </h1>

            <p className="mt-3 text-sm text-slate-600 dark:text-slate-400">
              Consultez votre formule, son échéance et
              vos documents contractuels.
            </p>
          </div>

          <button
            type="button"
            onClick={loadPurchases}
            disabled={loading}
            className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            <FaSyncAlt />
            Actualiser
          </button>
        </header>

        <section className="overflow-hidden rounded-[30px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="grid gap-6 p-5 sm:p-7 lg:grid-cols-[1fr_330px]">
            <div>
              <div className="flex flex-wrap items-center gap-3">
                <span
                  className={cn(
                    "inline-flex rounded-full px-3 py-1.5 text-xs font-bold uppercase tracking-wide",
                    tone.badge,
                  )}
                >
                  {subscription.label}
                </span>

                <span className="text-sm text-slate-500 dark:text-slate-400">
                  Achat n°{currentPurchase.id}
                </span>
              </div>

              <h2 className="mt-5 text-3xl font-black tracking-tight">
                {currentPurchase.formule}
              </h2>

              <div className="mt-2 text-2xl font-bold text-blue-600 dark:text-blue-400">
                {currencyFormatter.format(
                  Number(
                    currentPurchase.prix_total || 0,
                  ),
                )}
                <span className="ml-2 text-sm font-normal text-slate-500 dark:text-slate-400">
                  par an
                </span>
              </div>

              <div className="mt-7 grid gap-3 sm:grid-cols-2">
                <div className="rounded-2xl bg-slate-50 p-4 dark:bg-slate-950/60">
                  <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
                    <FaCalendarAlt />
                    Début
                  </div>

                  <div className="mt-2 font-bold">
                    {formatSubscriptionDate(
                      subscription.startDate,
                    )}
                  </div>
                </div>

                <div className="rounded-2xl bg-slate-50 p-4 dark:bg-slate-950/60">
                  <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
                    <FaClock />
                    Échéance
                  </div>

                  <div className="mt-2 font-bold">
                    {formatSubscriptionDate(
                      subscription.expiryDate,
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-7">
                <div className="mb-2 flex items-center justify-between gap-3">
                  <span className="text-sm font-semibold">
                    Période annuelle consommée
                  </span>

                  <span className="text-sm font-bold">
                    {subscription.progress} %
                  </span>
                </div>

                <div className="h-4 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
                  <div
                    className={cn(
                      "h-full rounded-full transition-all duration-700",
                      tone.bar,
                    )}
                    style={{
                      width: `${subscription.progress}%`,
                    }}
                  />
                </div>

                <div className="mt-3 text-sm text-slate-600 dark:text-slate-400">
                  {subscription.status === "expired"
                    ? `Abonnement expiré depuis ${Math.abs(subscription.daysLeft)} jour(s).`
                    : `${subscription.daysLeft} jour(s) restant(s) avant l’échéance.`}
                </div>
              </div>
            </div>

            <aside className="rounded-[24px] border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-950/60">
              <div
                className={cn(
                  "flex h-12 w-12 items-center justify-center rounded-2xl text-xl",
                  tone.icon,
                )}
              >
                {subscription.status === "active" ? (
                  <FaCheckCircle />
                ) : subscription.status ===
                  "expired" ? (
                  <FaExclamationTriangle />
                ) : (
                  <FaClock />
                )}
              </div>

              <h3 className="mt-4 text-lg font-black">
                Renouvellement
              </h3>

              {subscription.canRenew ? (
                <>
                  <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-400">
                    Votre abonnement peut maintenant être
                    reconduit pour une nouvelle année.
                  </p>

                  <button
                    type="button"
                    onClick={handleRenew}
                    disabled={renewing}
                    className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-blue-600 px-4 py-3 font-semibold text-white transition hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <FaRedoAlt />

                    {renewing
                      ? "Redirection..."
                      : "Reconduire ma formule"}
                  </button>
                </>
              ) : (
                <>
                  <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-400">
                    Le renouvellement en ligne sera
                    disponible 30 jours avant l’échéance.
                  </p>

                  <div className="mt-5 rounded-2xl bg-white px-4 py-3 text-center text-sm font-semibold text-slate-500 dark:bg-slate-900 dark:text-slate-400">
                    Encore {subscription.daysLeft} jours
                  </div>
                </>
              )}

              <button
                type="button"
                onClick={() =>
                  navigate("/formules-tarifs")
                }
                className="mt-3 inline-flex w-full items-center justify-center rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                Voir les autres formules
              </button>
            </aside>
          </div>
        </section>

        <section className="mt-8">
          <div>
            <h2 className="text-xl font-black">
              Mes documents
            </h2>

            <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
              Documents associés à votre abonnement
              actuel.
            </p>
          </div>

          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <DocumentCard
              icon={FaFileSignature}
              title="Devis de renouvellement"
              description="Téléchargez le devis préparé pour votre prochaine échéance, puis retournez-le signé."
              url={currentPurchase.devis_url}
              badge={
                currentPurchase.devis_statut ===
                "signe"
                  ? "Signé"
                  : currentPurchase.devis_url
                    ? "À signer"
                    : null
              }
            />

            <DocumentCard
              icon={FaFileInvoice}
              title="Facture finale"
              description="Votre facture définitive devient disponible après validation du règlement."
              url={currentPurchase.facture_url}
              badge={
                currentPurchase.facture_url
                  ? "Disponible"
                  : null
              }
            />
          </div>
        </section>

        <section className="mt-8">
          <div>
            <h2 className="text-xl font-black">
              Historique des achats
            </h2>

            <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
              Retrouvez vos précédents abonnements et
              documents.
            </p>
          </div>

          <div className="mt-4 overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
            <div className="hidden overflow-x-auto md:block">
              <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-800">
                <thead className="bg-slate-50 dark:bg-slate-950/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Date
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Formule
                    </th>

                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Montant
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Statut
                    </th>

                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Documents
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                  {purchaseHistory.map((purchase) => (
                    <tr key={purchase.id}>
                      <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-300">
                        {formatSubscriptionDate(
                          purchase.date_achat,
                        )}
                      </td>

                      <td className="px-4 py-3 font-semibold">
                        {purchase.formule}
                      </td>

                      <td className="px-4 py-3 text-right font-bold">
                        {currencyFormatter.format(
                          Number(
                            purchase.prix_total || 0,
                          ),
                        )}
                      </td>

                      <td className="px-4 py-3">
                        <span
                          className={cn(
                            "rounded-full px-2.5 py-1 text-xs font-semibold",
                            isPaidPurchase(purchase)
                              ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300"
                              : "bg-amber-100 text-amber-700 dark:bg-amber-500/10 dark:text-amber-300",
                          )}
                        >
                          {purchase.statut_reglement ||
                            "—"}
                        </span>
                      </td>

                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-2">
                          {purchase.devis_url ? (
                            <a
                              href={resolveDocumentUrl(
                                purchase.devis_url,
                              )}
                              target="_blank"
                              rel="noopener noreferrer"
                              title="Télécharger le devis"
                              className="rounded-xl border border-blue-200 bg-blue-50 p-2.5 text-blue-600 transition hover:bg-blue-100 dark:border-blue-900/50 dark:bg-blue-950/30 dark:text-blue-300"
                            >
                              <FaFileSignature />
                            </a>
                          ) : null}

                          {purchase.facture_url ? (
                            <a
                              href={resolveDocumentUrl(
                                purchase.facture_url,
                              )}
                              target="_blank"
                              rel="noopener noreferrer"
                              title="Télécharger la facture"
                              className="rounded-xl border border-emerald-200 bg-emerald-50 p-2.5 text-emerald-600 transition hover:bg-emerald-100 dark:border-emerald-900/50 dark:bg-emerald-950/30 dark:text-emerald-300"
                            >
                              <FaFileInvoice />
                            </a>
                          ) : null}

                          {!purchase.devis_url &&
                          !purchase.facture_url ? (
                            <span className="text-sm text-slate-400">
                              —
                            </span>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="divide-y divide-slate-200 dark:divide-slate-800 md:hidden">
              {purchaseHistory.map((purchase) => (
                <article
                  key={purchase.id}
                  className="p-4"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-bold">
                        {purchase.formule}
                      </div>

                      <div className="mt-1 text-xs text-slate-500">
                        {formatSubscriptionDate(
                          purchase.date_achat,
                        )}
                      </div>
                    </div>

                    <div className="font-bold">
                      {currencyFormatter.format(
                        Number(
                          purchase.prix_total || 0,
                        ),
                      )}
                    </div>
                  </div>

                  <div className="mt-4 flex flex-wrap gap-2">
                    {purchase.devis_url ? (
                      <a
                        href={resolveDocumentUrl(
                          purchase.devis_url,
                        )}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 rounded-xl bg-blue-50 px-3 py-2 text-sm font-semibold text-blue-600 dark:bg-blue-950/30 dark:text-blue-300"
                      >
                        <FaFileSignature />
                        Devis
                      </a>
                    ) : null}

                    {purchase.facture_url ? (
                      <a
                        href={resolveDocumentUrl(
                          purchase.facture_url,
                        )}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 rounded-xl bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-600 dark:bg-emerald-950/30 dark:text-emerald-300"
                      >
                        <FaFileInvoice />
                        Facture
                      </a>
                    ) : null}
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}